import {
  type HttpHandlerFn,
  type HttpInterceptorFn,
  type HttpRequest,
  provideHttpClient,
  withFetch,
  withInterceptors,
} from '@angular/common/http';
import {
  type EnvironmentProviders,
  Injectable,
  effect,
  inject,
  makeEnvironmentProviders,
  provideAppInitializer,
} from '@angular/core';
import {
  AutoRefreshTokenService,
  INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
  KEYCLOAK_EVENT_SIGNAL,
  KeycloakEventType,
  UserActivityService,
  includeBearerTokenInterceptor,
  provideKeycloak,
  withAutoRefreshToken,
} from 'keycloak-angular';
import Keycloak, {
  type KeycloakConfig,
  type KeycloakInitOptions,
} from 'keycloak-js';
import { catchError, throwError } from 'rxjs';

interface KeycloakEnv {
  keycloak: { url: string; realm: string; clientId: string };
  apiUrl: string;
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function buildKeycloakConfig(env: KeycloakEnv): KeycloakConfig {
  return {
    url: env.keycloak.url,
    realm: env.keycloak.realm,
    clientId: env.keycloak.clientId,
  };
}

export function buildKeycloakInitOptions(origin: string): KeycloakInitOptions {
  return {
    onLoad: 'check-sso',
    silentCheckSsoRedirectUri: `${origin}/assets/silent-check-sso.html`,
    pkceMethod: 'S256',
    checkLoginIframe: false,
  };
}

export function buildBearerTokenInterceptorConfig(apiUrl: string) {
  return [{ urlPattern: new RegExp(`^${escapeRegExp(apiUrl)}(/.*)?$`) }];
}

/** Preserve the current page as Keycloak post-login redirect target. */
export function buildSessionRecoveryLoginOptions(): {
  redirectUri: string;
} {
  return { redirectUri: window.location.href };
}

// On 401, force a token refresh. If it fails, KeycloakSessionRecovery picks up
// the AuthRefreshError event and re-triggers the login flow.
const unauthorizedInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
) => {
  const keycloak = inject(Keycloak);
  return next(req).pipe(
    catchError((error: { status?: number }) => {
      if (error.status === 401) {
        void keycloak.updateToken(0).catch(() => {});
      }
      return throwError(() => error);
    }),
  );
};

@Injectable({ providedIn: 'root' })
class KeycloakSessionRecovery {
  readonly #keycloak = inject(Keycloak);
  readonly #eventSignal = inject(KEYCLOAK_EVENT_SIGNAL);
  #loginTriggered = false;

  constructor() {
    effect(() => {
      switch (this.#eventSignal().type) {
        case KeycloakEventType.Ready:
        case KeycloakEventType.AuthSuccess:
        case KeycloakEventType.AuthRefreshSuccess:
          this.#loginTriggered = false;
          break;
        // TokenExpired is handled by AutoRefreshTokenService (silent updateToken).
        // Only redirect to login when that refresh definitively fails.
        case KeycloakEventType.AuthRefreshError:
          if (this.#loginTriggered) {
            break;
          }
          this.#loginTriggered = true;
          void this.#keycloak
            .login(buildSessionRecoveryLoginOptions())
            .catch(() => {
              this.#loginTriggered = false;
            });
          break;
      }
    });
  }
}

export function provideKeycloakAuth(env: KeycloakEnv): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideKeycloak({
      config: buildKeycloakConfig(env),
      // initOptions MUST be passed here. keycloak-angular only registers the
      // app-initializer that runs `feature.configure()` (which calls
      // AutoRefreshTokenService.start()) when `initOptions` is present. If we
      // instead init Keycloak in a separate provideAppInitializer, the
      // auto-refresh feature is never started, so the silent updateToken on
      // TokenExpired never runs and the access token simply expires.
      initOptions: buildKeycloakInitOptions(window.location.origin),
      features: [
        withAutoRefreshToken({
          // Application-level idle timeout. AutoRefreshTokenService treats
          // "no mouse/keyboard/scroll within sessionTimeout" as inactivity and
          // then runs onInactivityTimeout ('login' = full-page /auth redirect
          // that reloads the SPA and drops unsaved work). The old 5-min value
          // bounced users to login after any 5-min editing pause even though
          // the token refresh itself was succeeding (returns 200). 4h keeps
          // active users refreshing silently through normal pauses, while a
          // genuinely away-from-keyboard user (4h idle) is re-authenticated —
          // still within the realm ssoSessionIdleTimeout (8h). See #166.
          sessionTimeout: 14_400_000, // 4h idle timeout
          onInactivityTimeout: 'login',
        }),
      ],
      providers: [AutoRefreshTokenService, UserActivityService],
    }),
    provideAppInitializer(() => {
      inject(KeycloakSessionRecovery);
    }),
    provideHttpClient(
      withFetch(),
      withInterceptors([
        includeBearerTokenInterceptor,
        unauthorizedInterceptor,
      ]),
    ),
    {
      provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
      useValue: buildBearerTokenInterceptorConfig(env.apiUrl),
    },
  ]);
}
